import react, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, startAfter, where } from 'firebase/firestore';
import { db } from '../../firebase';
import '../../Styles/Auctions/auctionPageStyle.css';

const auctionDb = "AuctionItems"
// const auctionDb = "TestAuctionItems"

function AuctionsPage() {
    const [auctionItems, setAuctionItems] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState(''); 
    const [locationFilter, setLocationFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categories, setCategories] = useState([]); 
    const [locations, setLocations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const currentTime = new Date();

    useEffect(() => {
        const fetchAuctionItems = async () => {
            try {
                const allSnapshot = await getDocs(
                    query(collection(db, auctionDb), where('isApproved', '==', true))
                );
    
                const allAuctions = allSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                console.log(allAuctions);
    
                const normalizeDate = (date) => {
                    const normalized = new Date(date);
                    normalized.setHours(0, 0, 0, 0);
                    return normalized;
                };
    
                const now = new Date();
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(now.getDate() - 7);
                const normalizedSevenDaysAgo = normalizeDate(sevenDaysAgo);
    
                const filteredAuctions = allAuctions.filter(auction => {
                    const roundKeys = Object.keys(auction.Rounds || {}).filter(key =>
                        key.startsWith('Round')
                    );
                    if (roundKeys.length === 0) return true;
    
                    const sortedRounds = roundKeys
                        .map(key => auction.Rounds[key])
                        .sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
    
                    const lastRound = sortedRounds[sortedRounds.length - 1];
                    const lastRoundEndDate = lastRound?.endDate ? new Date(lastRound.endDate) : null;
    
                    if (!lastRoundEndDate) return true;
    
                    const normalizedEndDate = normalizeDate(lastRoundEndDate);
    
                    return normalizedEndDate >= normalizedSevenDaysAgo || normalizedEndDate > now;
                });
    
                // Sort the auctions by Rounds.Round1.Date (if it exists)
                filteredAuctions.sort((a, b) => {
                    const dateA = a.Rounds?.Round1?.Date ? new Date(a.Rounds.Round1.Date) : new Date(0);
                    const dateB = b.Rounds?.Round1?.Date ? new Date(b.Rounds.Round1.Date) : new Date(0);
                    return dateB - dateA; // Descending
                });
                
                const uniqueCategories = [...new Set(filteredAuctions.map(item => item.Category))];
                const uniqueLocations = [...new Set(filteredAuctions.map(item => item.Location))];
    
                setAuctionItems(filteredAuctions);
                setCategories(uniqueCategories);
                setLocations(uniqueLocations);
            } catch (error) {
                console.error('Error fetching auction items:', error);
            }
        };
    
        fetchAuctionItems();
    }, []);

    const getAuctionStatus = (auction) => {
        const currentTime = new Date();
        const rounds = auction.Rounds;
        if (!rounds) return 'Upcoming';

        const roundEntries = Object.entries(rounds)
            .map(([key, round]) => {
                const start = round?.Date ? new Date(round.Date) : null;
                const end = round?.endDate ? new Date(round.endDate) : null;
                return { start, end };
            })
            // Keep only valid date entries
            .filter(round => round.start instanceof Date && !isNaN(round.start));

        // 🟢 No valid dates at all → Upcoming
        if (roundEntries.length === 0) return 'Upcoming';

        const isLive = roundEntries.some(
            round => round.start && round.end && currentTime >= round.start && currentTime <= round.end
        );
        if (isLive) return 'Live';

        const firstRoundStart = Math.min(...roundEntries.map(round => round.start.getTime()));
        if (currentTime < firstRoundStart) return 'Upcoming';

        return 'Completed';
    };

    const filteredAuctions = auctionItems.filter((auction) => {
        const auctionStatus = getAuctionStatus(auction);
        const matchesSearch = auction.Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              auction.Location?.toLowerCase().includes(searchQuery.toLowerCase());
        
        return (
            auction.isApproved === true &&
            matchesSearch &&
            (categoryFilter ? auction.Category === categoryFilter : true) &&
            (locationFilter ? auction.Location === locationFilter : true) &&
            (statusFilter ? auctionStatus === statusFilter : true)
        );
    });
    

    return (
        <div className="AuctionPage">
            <div className="auctionPageControls">
                <div className="sectionKicker">Browse</div>
                <h1 className="sectionTitle">Auctions</h1>
                <div className="controls">
                    <label>
                        <div className="searchContainer">
                            <input
                                type="text"
                                placeholder="Search by name or location..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="auctionSearchBar"
                            />
                        </div>
                    </label>

                    <label>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="">All Categories</option>
                            {categories.map((category) => (
                                <option key={category} value={category}>
                                    {category}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        <select
                            value={locationFilter}
                            onChange={(e) => setLocationFilter(e.target.value)}
                        >
                            <option value="">All Locations</option>
                            {locations.map((location) => (
                                <option key={location} value={location}>
                                    {location}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="Live">Live</option>
                            <option value="Upcoming">Upcoming</option>
                        </select>
                    </label>
                </div>

                <div className="sectionKicker">Results</div>
            </div>
            
            <div className="auctionContainer">
                {filteredAuctions.length > 0 ? (
                    filteredAuctions.map((item) => (
                        <Link
                            key={item.id} 
                            to={`/auctions/${item.id}`}
                            style={{ textDecoration: 'none' }}
                        >
                            <div key={item.id} className="auctionItemCard">
                                <img
                                    src={item.Photos[0]}
                                    alt={`Auction item`}
                                    className="auctionPhoto"
                                />
                                <div className="cardText">
                                    <h2>{item.Name}</h2>
                                    <p>{item.Location}</p>
                                </div>
                            </div>
                        </Link>
                    ))
                ) : (
                    <p>No Auction Items Found.</p>
                )}
            </div>
            
            <Link 
                to={`/create-auction`}
                style={{ textDecoration: 'none' }}
            >
                <button className="createItemBtn">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z"/>
                    </svg>
                    Create Auction
                </button>
            </Link>
        </div>
    )
}

export default AuctionsPage;